import React, { useContext } from 'react';
import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useMutation } from '@apollo/client';
import * as Yup from 'yup';
import get from 'lodash/get';

import Input from '../../../components/Input';
import { ENSURE_PARTNER_ACCESS_POINTS, EnsurePartnerAccessPointsMutation } from '../query';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { useSpinner } from '../../../contexts/SpinnerContext';
import UserContext from '../../../contexts/UserContext';

interface EnsurePartnerAccessPointsModalProps {
  isOpen: boolean;
  onClose: () => void;
  dropPointId: string;
  refetch: () => Promise<any> | void;
  refetchState?: () => Promise<any> | void;
}

const EnsurePartnerAccessPointsSchema = Yup.object().shape({
  comment: Yup.string().required('Du musst einen Kommentar/Begründung eingeben'),
});

const EnsurePartnerAccessPointsModal = ({ isOpen, onClose, dropPointId, refetch, refetchState }: EnsurePartnerAccessPointsModalProps) => {
  const { user: internalUser } = useContext(UserContext);
  const { toggleSpinner } = useSpinner();
  const toast = useCustomToast();
  const [ensurePartnerAccessPoints, { loading }] = useMutation<EnsurePartnerAccessPointsMutation>(ENSURE_PARTNER_ACCESS_POINTS);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Access Points anlegen</ModalHeader>
        <Formik
          initialValues={{ comment: '' }}
          validationSchema={EnsurePartnerAccessPointsSchema}
          onSubmit={async ({ comment }) => {
            try {
              toggleSpinner(true);
              const result = await ensurePartnerAccessPoints({
                variables: { dropPointId, comment, internalUserId: internalUser.id },
              });
              await Promise.all([Promise.resolve(refetch()), Promise.resolve(refetchState?.())]);
              const provisioning = result.data?.ensurePartnerAccessPoints;
              toast({
                title: 'Erfolg!',
                description: `UPS: ${provisioning?.accessPointOutcome || '—'}, Amazon: ${provisioning?.hubCounterOutcome || '—'}.`,
                status: 'success',
              });
              onClose();
            } catch (error) {
              toast({
                description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
                status: 'error',
              });
            } finally {
              toggleSpinner(false);
            }
          }}
        >
          <Form>
            <ModalBody>
              <Text fontSize="sm" mb="4">
                Es werden nur die fehlenden Partner-Datensätze angelegt. Bereits vorhandene UPS- oder Amazon-Einträge und bestehende
                Statuswerte bleiben unverändert.
              </Text>
              <Input name="comment" label="Kommentar/Begründung" />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" size="sm" colorScheme="red" type="button" onClick={onClose}>
                Abbrechen
              </Button>
              <Button ml="4" size="sm" colorScheme="brand" type="submit" isLoading={loading}>
                Anlegen
              </Button>
            </ModalFooter>
          </Form>
        </Formik>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default EnsurePartnerAccessPointsModal;
